import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";

const Files = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchFiles();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "FILES") {
      fetchFiles();
    }
  }, [modal, modal?.isUpdated?.isUpdatedIn]);

  // Fetch all files
  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/knowledge-base", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (response?.data && Array.isArray(response.data)) {
        const mappedFiles = response.data.map((item) => ({
          id: item._id,
          name: item.file_name,
          type: item.file_type,
          size: item.file_size,
          sizeBytes: parseSizeToBytes(item.file_size),
          uploadedAt: formatDate(item.uploaded_at),
          uploadedBy: item.uploaded_by,
          status: item.status,
          downloads: item.downloads || 0,
          filePath: item.file_path,
        }));

        setFiles(mappedFiles);
        setPaginationInfo({
          total_count: response.pagination?.total_count || 0,
          total_pages: response.pagination?.total_pages || 0,
        });
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      toast.error("Failed to load files");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch used-by details
  const fetchUsedByDetails = async (fileId) => {
    try {
      const response = await apiClient.get(`/knowledge-base/${fileId}/used-by`);

      let usedByData = {
        fileId,
        usedBy: [],
      };

      if (response?.data && Array.isArray(response.data)) {
        const usedByNames = response.data.map((item) => item.name);
        usedByData = {
          fileId,
          usedBy: usedByNames,
        };
      }

      setViewDetailsData(usedByData);
      return usedByData;
    } catch (error) {
      console.error("Error fetching used-by details:", error);
      toast.error("Failed to load usage details");
      const errorData = {
        fileId,
        usedBy: [],
      };
      setViewDetailsData(errorData);
      return errorData;
    }
  };

  // View file details
  const handleViewFile = async (file) => {
    const usedByData = await fetchUsedByDetails(file.id);
    dispatch(
      setModal({
        type: "VIEW_FILE_DETAILS",
        data: {
          ...file,
          viewDetailsData: usedByData, // Use the returned data directly
          onRemoveUsedBy: handleRemoveUsedBy, // Pass remove handler to modal
        },
      })
    );
  };

  // Download file
  const handleDownloadFile = async (file) => {
    try {
      // Use axios directly for blob download
      const response = await apiClient.get(
        `/knowledge-base/${file.id}/download`,
        {},
        { responseType: 'blob' }
      );

      // Create blob link to download
      const blob = new Blob([response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${file.name}`);

      // Refresh file list to update download count
      await fetchFiles();
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // Delete file
  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete file "${file.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/knowledge-base/${file.id}`);

      toast.success("File deleted successfully");

      // Refresh file list automatically from server
      await fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(error?.response?.data?.message || "Failed to delete file");
    }
  };

  // Remove used-by entry (local state only)
  const handleRemoveUsedBy = (fileId, assistantName) => {
    if (viewDetailsData?.fileId === fileId) {
      setViewDetailsData({
        ...viewDetailsData,
        usedBy: viewDetailsData.usedBy.filter((name) => name !== assistantName),
      });
    }
  };

  // Helper: Parse size string to bytes
  const parseSizeToBytes = (sizeString) => {
    if (!sizeString) return 0;

    const match = sizeString.match(/^([\d.]+)\s*([A-Z]+)$/i);
    if (!match) return 0;

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    const units = {
      B: 1,
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024,
    };

    return value * (units[unit] || 0);
  };

  // Helper: Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "PDF":
        return <PictureAsPdfIcon sx={{ color: "#D32F2F" }} />;
      case "DOCX":
        return <ArticleIcon sx={{ color: "#1976D2" }} />;
      case "TXT":
      case "MD":
      case "CSV":
        return <DescriptionIcon sx={{ color: "#666" }} />;
      default:
        return <DescriptionIcon sx={{ color: "#666" }} />;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      PDF: { bg: "#FFEBEE", color: "#C62828" },
      DOCX: { bg: "#E3F2FD", color: "#1565C0" },
      TXT: { bg: "#F5F5F5", color: "#666" },
      CSV: { bg: "#E8F5E9", color: "#2E7D32" },
      MD: { bg: "#FFF3E0", color: "#E65100" },
    };
    return colors[type] || { bg: "#F5F5F5", color: "#666" };
  };

  const getStatusColor = (status) => {
    const colors = {
      Indexed: "success",
      Processing: "warning",
      Failed: "error",
    };
    return colors[status] || "default";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "File Name",
        size: 250,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getFileIcon(row.original.type)}
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Uploaded by {row.original.uploadedBy}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 80,
        Cell: ({ cell }) => {
          const colors = getTypeColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "size",
        header: "Size",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={getStatusColor(cell.getValue())}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "uploadedAt",
        header: "Uploaded",
        size: 140,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "downloads",
        header: "Downloads",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalFiles = paginationInfo.total_count;
  const totalSize = files.reduce((sum, f) => sum + f.sizeBytes, 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
  const indexedFiles = files.filter((f) => f.status === "Indexed").length;
  const totalDownloads = files.reduce((sum, f) => sum + f.downloads, 0);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F9FAFB" }}>
      {/* Header Section - White Background */}
      <Box sx={{
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E5E7EB",
        px: 4,
        py: 3
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              Knowledge Base Files
            </Typography>
            <Typography variant="body2" color="#6B7280">
              Upload and manage files to enhance AI assistant knowledge
            </Typography>
          </Box>
          <CustomButton
            icon={<UploadFileIcon />}
            iconPosition="left"
            onClick={() => dispatch(setModal({ type: "UPLOAD_FILE" }))}
            text="Upload File"
          />
        </Box>
      </Box>

      {/* Stats Cards Section - Separate */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              {totalFiles}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Files
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#3B82F6" sx={{ mb: 0.5 }}>
              {totalSizeMB} MB
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Storage Used
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#10B981" sx={{ mb: 0.5 }}>
              {indexedFiles}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Indexed Files
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#F59E0B" sx={{ mb: 0.5 }}>
              {totalDownloads}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Downloads
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          p: 2
        }}>
          <MaterialReactTable
            columns={columns}
            data={files}
            manualPagination
            rowCount={paginationInfo.total_count}
            onPaginationChange={setPagination}
            state={{
              isLoading: loading,
              pagination: pagination,
            }}
            enableRowActions
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={() => handleViewFile(row.original)}
                    sx={{ color: "info.main" }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={() => handleDownloadFile(row.original)}
                    sx={{ color: "primary.main" }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile(row.original)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            muiTablePaginationProps={{
              showFirstButton: true,
              showLastButton: true,
            }}
            renderEmptyRowsFallback={() => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  px: 2,
                }}
              >
                <DescriptionIcon sx={{ fontSize: 64, color: "#E0E0E0", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No files uploaded yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload your first file to get started with the knowledge base
                </Typography>
              </Box>
            )}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                borderRadius: 0,
                border: "none",
                boxShadow: "none"
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F9FAFB",
                fontWeight: 600,
              },
            }}
            muiTableBodyProps={{
              sx: {
                '& tr:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Files;
